
import React, { useState, useRef, useEffect } from 'react';
import { useApp } from './AppContext';
import { Button } from './Components';

export const HealthAssistant: React.FC = () => {
  const { askClinicalAI, globalSettings } = useApp();
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState<{role: 'user' | 'ai', text: string}[]>([]);
  const [input, setInput] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, isTyping]);

  // Do not render if disabled by platform core - moved after hooks to prevent React error #300
  if (!globalSettings.aiEngineActive) return null;

  const handleSend = async () => {
    if (!input.trim()) return;
    const userMsg = input;
    setInput('');
    setMessages(prev => [...prev, { role: 'user', text: userMsg }]);
    setIsTyping(true);

    try {
      const response = await askClinicalAI(userMsg);
      setMessages(prev => [...prev, { role: 'ai', text: response }]);
    } catch (e) {
      setMessages(prev => [...prev, { role: 'ai', text: "I'm having trouble connecting to the clinical brain right now. Please try again later." }]);
    } finally {
      setIsTyping(false);
    }
  };

  return (
    <div className="fixed bottom-10 right-10 z-[500] font-['Plus_Jakarta_Sans']">
      {!isOpen ? (
        <button 
          onClick={() => setIsOpen(true)}
          aria-label="Open Clinical AI Assistant"
          aria-expanded="false"
          className="w-20 h-20 bg-gradient-to-br from-indigo-600 to-indigo-500 rounded-[2rem] shadow-2xl shadow-indigo-500/40 flex items-center justify-center text-white text-3xl hover:scale-110 hover:rotate-12 transition-all duration-500 animate-float"
        >
          <i className="fa-solid fa-robot" aria-hidden="true"></i>
          <span className="absolute -top-1 -right-1 w-6 h-6 bg-emerald-500 border-4 border-white rounded-full" aria-hidden="true"></span>
        </button>
      ) : (
        <div 
          role="dialog" 
          aria-label="Clinical AI Assistant Dialog" 
          className="w-[calc(100vw-40px)] md:w-[450px] max-h-[calc(100vh-140px)] h-[600px] bg-white/95 backdrop-blur-2xl rounded-[3rem] shadow-2xl border border-indigo-50 flex flex-col overflow-hidden animate-in zoom-in-95 fade-in duration-300"
        >
          <div className="p-8 bg-gradient-to-r from-indigo-600 to-indigo-500 text-white flex justify-between items-center">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-white/20 rounded-2xl flex items-center justify-center text-xl backdrop-blur-md">
                <i className="fa-solid fa-notes-medical" aria-hidden="true"></i>
              </div>
              <div>
                <h4 className="font-black text-sm uppercase tracking-widest">Clinical AI Assistant</h4>
                <p className="text-[10px] text-white/70 font-bold uppercase tracking-widest">Powered by Gemini 3</p>
              </div>
            </div>
            <button 
              onClick={() => setIsOpen(false)} 
              aria-label="Close Clinical AI Assistant"
              className="text-white/60 hover:text-white transition-colors"
            >
              <i className="fa-solid fa-xmark text-xl" aria-hidden="true"></i>
            </button>
          </div>

          <div ref={scrollRef} className="flex-grow overflow-y-auto p-8 space-y-6 scrollbar-hide">
            {messages.length === 0 && (
              <div className="text-center py-10">
                <div className="w-20 h-20 bg-indigo-50 rounded-full flex items-center justify-center text-indigo-400 mx-auto mb-6 text-3xl">
                  <i className="fa-solid fa-comment-medical" aria-hidden="true"></i>
                </div>
                <h5 className="text-slate-900 font-black text-lg mb-2">How can I assist your health journey?</h5>
                <p className="text-slate-400 text-sm font-medium px-6">Ask me about symptoms, medical terminology, or generic drug information.</p>
                <div className="mt-6 p-4 bg-amber-50 rounded-2xl border border-amber-100 text-[10px] font-bold text-amber-700 leading-relaxed mx-4">
                  <i className="fa-solid fa-triangle-exclamation mr-2" aria-hidden="true"></i>
                  DISCLAIMER: This tool is for informational purposes only and is NOT a substitute for professional medical advice.
                </div>
              </div>
            )}
            {messages.map((m, i) => (
              <div key={i} className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                <div className={`max-w-[85%] p-6 rounded-[2rem] text-sm font-medium leading-relaxed shadow-sm ${
                  m.role === 'user' 
                    ? 'bg-indigo-600 text-white rounded-tr-none' 
                    : 'bg-slate-50 text-slate-800 border border-slate-100 rounded-tl-none'
                }`}>
                  {m.text}
                </div>
              </div>
            ))}
            {isTyping && (
              <div className="flex justify-start">
                <div className="bg-slate-50 p-6 rounded-[2rem] rounded-tl-none border border-slate-100 flex gap-2">
                  <span className="w-2 h-2 bg-indigo-400 rounded-full animate-bounce"></span>
                  <span className="w-2 h-2 bg-indigo-400 rounded-full animate-bounce delay-100"></span>
                  <span className="w-2 h-2 bg-indigo-400 rounded-full animate-bounce delay-200"></span>
                </div>
              </div>
            )}
          </div>

          <div className="p-8 border-t border-slate-50 bg-slate-50/50">
            <div className="relative">
              <input 
                className="w-full p-6 pr-16 bg-white rounded-3xl border-2 border-indigo-50 focus:border-indigo-600 outline-none font-bold text-slate-900 transition-all shadow-sm"
                placeholder="Type clinical query..."
                aria-label="Ask Clinical AI Assistant a question"
                value={input}
                onChange={e => setInput(e.target.value)}
                onKeyPress={e => e.key === 'Enter' && handleSend()}
              />
              <button 
                onClick={handleSend}
                aria-label="Send message to AI Assistant"
                className="absolute right-4 top-1/2 -translate-y-1/2 w-12 h-12 bg-indigo-600 text-white rounded-2xl flex items-center justify-center hover:bg-indigo-700 transition-all shadow-lg shadow-indigo-500/20"
              >
                <i className="fa-solid fa-paper-plane" aria-hidden="true"></i>
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
